/**
 * Canonical vendor types for filtering.
 */
export type VendorType = 'Optimizely' | 'Partner';

/**
 * Utility class for normalizing vendor strings to canonical vendor types.
 *
 * This provides a centralized, extensible way to categorize apps by their vendor,
 * handling various spellings and legacy names for Optimizely.
 */
export class VendorMapper {
  /**
   * Normalize a vendor string to a canonical VendorType.
   *
   * @param vendor The raw vendor string from app metadata
   * @returns 'Optimizely' if vendor matches known Optimizely names, otherwise 'Partner'
   */
  public static normalize(vendor: string | undefined): VendorType {
    // Handle missing vendor - default to Partner
    if (!vendor) {
      return 'Partner';
    }

    const normalizedVendor = vendor.toLowerCase().trim();

    // Check if vendor contains any Optimizely-related keywords
    for (const keyword of this.OPTIMIZELY_VENDORS) {
      if (normalizedVendor.includes(keyword)) {
        return 'Optimizely';
      }
    }

    return 'Partner';
  }

  /**
   * Get all available vendor types for display in the UI.
   *
   * @returns Array of all VendorType values
   */
  public static getAllVendorTypes(): VendorType[] {
    return ['Optimizely', 'Partner'];
  }

  /**
   * Add a new vendor name that should be normalized to "Optimizely".
   * This allows for runtime extension of the Optimizely vendor list.
   *
   * @param vendorName The vendor name to add (will be lowercased automatically)
   */
  public static addOptimizelyVendor(vendorName: string): void {
    this.OPTIMIZELY_VENDORS.add(vendorName.toLowerCase());
  }

  /**
   * Check if a vendor string would be normalized to Optimizely.
   *
   * @param vendor The vendor string to check
   * @returns true if vendor would be normalized to 'Optimizely'
   */
  public static isOptimizely(vendor: string | undefined): boolean {
    return this.normalize(vendor) === 'Optimizely';
  }

  /**
   * Set of vendor name keywords that should be normalized to "Optimizely".
   * All comparisons are case-insensitive.
   */
  private static readonly OPTIMIZELY_VENDORS = new Set([
    'optimizely',
    'zaius', // Legacy Optimizely brand name
  ]);
}
